import { createSelector } from 'reselect'
import { IS_DUAL, IS_ELEC, IS_GAS, IS_PARTICULAR, IS_PROFESSIONAL, IS_PROPANE } from './Common/constants'
import { parseTab } from '@/shared/tools/activeUtil'
import { capitalize } from '@/shared/tools/stringFunctions'
import { selectIsGryvLeadSource } from '@/store/PageConfiguration/PageConfigurationSelectors'

const GRYV_EXCLUDED_TECHNICAL_NAMES = ['edf', 'plenitude']

export const selectSubscriptionForm = state => state.formV2
// USED ONES
// example to fetch a given data: useSelector(selectAdditionalSaleDataByName('voltalis'))
export const selectAdditionalSaleDataByName = name => state => state.formV2.additionalSales[name] || {}
export const selectAllAdditionalSaleData = state => state.formV2.additionalSales || {}
export const selectAdditionalSalesConfigByName = name => state => state.formV2.additionalSalesConfig.sales[name] || {}
export const selectAdditionalSalesConfig = state => state.formV2.additionalSalesConfig || {}
export const selectAddressById = addressId => state => state.formV2.addresses[addressId] || {}
export const selectSubscriptionFormData = createSelector(selectSubscriptionForm, data => data)
export const selectOpenedContracts = createSelector(selectSubscriptionForm, data => data.openedContracts)
export const selectExistingContracts = createSelector(selectSubscriptionForm, data => data.contracts)
export const selectGroupedContractsByDate = createSelector(selectExistingContracts, contracts => {
  const groups = {}

  Object.values(contracts || {}).forEach(contract => {
    if (!contract?.createdAt) return
    const date = contract.createdAt.split(' ')[0]
    groups[date] = groups[date] || []
    groups[date].push(contract)
  })

  return Object.entries(groups)
    .map(([date, contracts]) => ({ date, contracts }))
    .sort((a, b) => new Date(a.date) - new Date(b.date))
})
export const selectContactSubscriptionData = createSelector(selectSubscriptionForm, data => data.contact)
export const selectContactComments = createSelector(selectSubscriptionForm, data => data.comments)
export const selectPersonalContactSubscriptionData = createSelector(
  selectContactSubscriptionData,
  data => data.personal,
)
export const selectPreferencesContactSubscriptionData = createSelector(
  selectContactSubscriptionData,
  data => data.preferences,
)
export const selectProfessionalContactSubscriptionData = createSelector(
  selectContactSubscriptionData,
  data => data.professional,
)
export const selectIdentityContactSubscriptionData = createSelector(
  selectContactSubscriptionData,
  data => data.identity,
)
export const selectContactConfig = createSelector(selectContactSubscriptionData, data => data.config)
export const selectContactFormErrors = createSelector(selectContactConfig, data => data.errors ?? {})
export const selectContactIsDirty = createSelector(selectContactConfig, data => data?.isDirty === true)
export const selectAdditionalContactSubscriptionData = createSelector(
  selectContactSubscriptionData,
  data => data.additional,
)
export const selectRecentBankings = createSelector(selectSubscriptionForm, data => data.recentBankings ?? [])
export const selectConfig = createSelector(selectSubscriptionForm, data => data.config)
export const selectActiveTab = createSelector(selectConfig, data => data.activeTab ?? '')
export const selectActiveAddress = createSelector(selectConfig, data => data.activeAddress)
export const selectActiveTabParts = createSelector(selectActiveTab, activeTab => parseTab(activeTab))
export const selectActiveTabId = createSelector(selectActiveTabParts, parts => parts.id)
export const selectActiveTabType = createSelector(selectActiveTabParts, parts => capitalize(parts.type))
export const selectCurrentContractActive = createSelector(
  selectActiveTabId,
  selectExistingContracts,
  (activeTabId, contracts) => contracts[activeTabId] ?? {},
)

export const selectCharacteristicsCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.characteristics ?? {},
)

export const selectConsumptionCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.consumption ?? {},
)

export const selectContractDetailsCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.contractDetails ?? {},
)

export const selectBillingCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.billing ?? {},
)

export const selectElecCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.elec ?? {},
)

export const selectGasCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.gas ?? {},
)

export const selectPropaneCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.propane ?? {},
)

export const selectConfigCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.config ?? {},
)

export const selectOfferConfigCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.config?.offerConfiguration ?? {},
)

export const selectProviderConfigCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.config?.providerConfiguration ?? {},
)

export const selectOfferAttributesCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.config?.offerConfiguration?.attributes ?? {},
)

export const isParticularContact = createSelector(selectPreferencesContactSubscriptionData, preferences => {
  const clientType = preferences.clientType ?? false
  return clientType === IS_PARTICULAR
})

export const isProfessionalContact = createSelector(selectPreferencesContactSubscriptionData, preferences => {
  const clientType = preferences.clientType ?? false
  return clientType === IS_PROFESSIONAL
})

export const isGasContract = createSelector(selectCurrentContractActive, currentContract => {
  const energyType = currentContract?.characteristics?.energyType ?? false
  return energyType === IS_GAS
})

export const isElecContract = createSelector(selectCurrentContractActive, currentContract => {
  const energyType = currentContract?.characteristics?.energyType ?? false
  return energyType === IS_ELEC
})

export const isDualContract = createSelector(selectCurrentContractActive, currentContract => {
  const energyType = currentContract?.characteristics?.energyType ?? false
  return energyType === IS_DUAL
})

export const isPropaneContract = createSelector(selectCurrentContractActive, currentContract => {
  const energyType = currentContract?.characteristics?.energyType ?? false
  return energyType === IS_PROPANE
})

export const isGasOrDualContract = createSelector(isGasContract, isDualContract, (isGasContract, isDualContract) => {
  return isGasContract || isDualContract
})

export const selectAddressesData = createSelector(selectSubscriptionForm, data => data.addresses ?? {})

export const selectAddressCurrentContractActive = createSelector(
  selectCurrentContractActive,
  selectAddressesData,
  (currentContract, addreses) => addreses[currentContract?.addressId] ?? {},
)

export const selectAddressesConfigData = createSelector(selectAddressesData, data => data.config ?? {})
export const selectAddressesErrorsData = createSelector(selectAddressesConfigData, data => data.errors ?? {})
export const selectAddressesDirtyMap = createSelector(
  selectAddressesConfigData,
  data => data.dirtyMap ?? {},
)
// True when at least one address (any) has unsaved field changes.
export const selectAnyAddressIsDirty = createSelector(selectAddressesDirtyMap, dirtyMap =>
  Object.values(dirtyMap).some(Boolean),
)
// True when at least one electricityMeter (any address) is dirty.
export const selectAnyElectricityMeterIsDirty = createSelector(selectAddressesData, addresses =>
  Object.entries(addresses).some(
    ([key, address]) => key !== 'config' && address?.electricityMeter?.isDirty === true,
  ),
)
// True when at least one gasMeter (any address) is dirty.
export const selectAnyGasMeterIsDirty = createSelector(selectAddressesData, addresses =>
  Object.entries(addresses).some(([key, address]) => key !== 'config' && address?.gasMeter?.isDirty === true),
)

export const selectFormattedAddressList = createSelector(selectAddressesConfigData, data => {
  const addressList = data.addressList ?? []
  return addressList.map(address => ({
    id: address.label,
    label: address.label,
    address: address.streetName,
    streetName: address.streetName,
    streetNumber: address.houseNumber,
    houseNumber: address.houseNumber,
    city: address.city,
    inseeCode: address.inseeCode,
    zipcode: address.zipcode,
    type: address.type,
    context: address.context,
  }))
})
export const selectCurrentAddressData = createSelector(
  selectActiveAddress,
  selectAddressesData,
  (activeAddress, addresses) => addresses[activeAddress ?? 'new'] ?? {},
)
export const selectCurrentAddressErrors = createSelector(
  selectActiveAddress,
  selectAddressesErrorsData,
  (activeAddress, addressesErrorsData) => addressesErrorsData[activeAddress] ?? {},
)

// ── Active-address-scoped dirty selectors ────────────────────────────────
// CLIENT_TAB_SAVE_ALL only operates on the currently active address and its
// meters (mirrors the original per-module save buttons). The Save button and
// the warning icon use these scoped selectors so the button only enables
// when the active address actually has something to save, and the warning
// only shows for unsaved changes that can be reached by clicking Save.
// IMPORTANT: this block depends on `selectCurrentAddressData` (defined right
// above). Do NOT hoist it earlier in this file ─ that triggers a TDZ error
// ("Cannot access 'selectCurrentAddressData' before initialization") as soon
// as any consumer imports the module.
export const selectActiveAddressIsDirty = createSelector(
  selectActiveAddress,
  selectAddressesDirtyMap,
  (activeAddress, dirtyMap) => dirtyMap[activeAddress ?? 'new'] === true,
)
export const selectActiveElectricityMeterIsDirty = createSelector(
  selectCurrentAddressData,
  data => data?.electricityMeter?.isDirty === true,
)
export const selectActiveGasMeterIsDirty = createSelector(
  selectCurrentAddressData,
  data => data?.gasMeter?.isDirty === true,
)

// Aggregated dirty state for the 'client' tab — scoped to what the global
// Save button can actually save (contact + active address + its meters).
// Drives both the warning icon and the Save button's enabled state.
export const selectClientTabHasUnsavedChanges = createSelector(
  selectContactIsDirty,
  selectActiveAddressIsDirty,
  selectActiveElectricityMeterIsDirty,
  selectActiveGasMeterIsDirty,
  (contactDirty, addressDirty, elecDirty, gasDirty) => contactDirty || addressDirty || elecDirty || gasDirty,
)

export const selectCityEligibilityData = createSelector(
  selectAddressCurrentContractActive,
  selectAddressesData,
  selectAddressesConfigData,
  (currentContractAddress, addresses, addressesConfig) => {
    return {
      cityEligibility: currentContractAddress?.cityEligibility ?? {},
      cityEligibilityIsLoading: addressesConfig?.addressCityEligibilityIsLoading ?? false,
    }
  },
)

export const selectPdlSearchData = createSelector(selectSubscriptionForm, data => data.pdlSearchData ?? {})
export const selectPceSearchData = createSelector(selectSubscriptionForm, data => data.pceSearchData ?? {})
export const selectCommunicationBoardState = createSelector(
  selectSubscriptionForm,
  form => form?.communicationBoard || {},
)

export const selectScheduledCallbackState = createSelector(
  selectSubscriptionForm,
  form => form?.scheduledCallback || {},
)

export const selectScheduledCallbackForm = createSelector(
  selectScheduledCallbackState,
  callback => callback?.form || {},
)

export const selectScheduledCallbackUpcoming = createSelector(
  selectScheduledCallbackState,
  callback => callback?.upcoming || {},
)
export const selectHasScheduledCallbackUpcoming = createSelector(
  selectScheduledCallbackUpcoming,
  upcoming => Array.isArray(upcoming.items) && upcoming.items.length > 0,
)

export const selectClubSelectraState = createSelector(selectSubscriptionForm, form => form?.clubSelectra || {})
export const selectGryvChecklist = createSelector(selectSubscriptionForm, form => form?.gryvChecklist ?? {})

export const selectGryvFilteredProviders = createSelector(
  selectIsGryvLeadSource,
  selectConfigCurrentContractActive,
  (isGryv, config) => {
    const providers = config?.providers ?? {}

    if (!isGryv) return providers

    return Object.fromEntries(
      Object.entries(providers).filter(
        ([, provider]) => !GRYV_EXCLUDED_TECHNICAL_NAMES.includes(provider?.integration_technical_name),
      ),
    )
  },
)
